/**
 * ContactsPage Component
 *
 * Страница со списком всех контактов.
 * Фильтрация по ролям происходит на бэкенде.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Title,
  Text,
  Group,
  Badge,
  Anchor,
  Loader,
  Center,
  Alert,
  Pagination,
  Select,
  Stack,
} from '@mantine/core';
import {
  IconPhone,
  IconMail,
  IconStar,
  IconAlertCircle,
  IconBrandTelegram,
  IconBrandWhatsapp,
} from '@tabler/icons-react';
import DataTable from '../common/DataTable';
import { formatPhoneDisplay } from '../../utils/phoneUtils';
import tokens from '../../theme/designTokens';

// Колонки таблицы
const columns = [
  { key: 'full_name', label: 'ФИО', sortable: true },
  { key: 'organization_name', label: 'Организация', sortable: true },
  { key: 'position', label: 'Должность', sortable: true },
  { key: 'phone', label: 'Телефон', sortable: false },
  { key: 'email', label: 'Email', sortable: true },
  { key: 'messengers', label: 'Мессенджеры', sortable: false },
];

// Поля для поиска
const searchFields = ['full_name', 'organization_name', 'position', 'email', 'phone_search'];

/**
 * Форматирование ФИО
 */
const formatFullName = (contact) => {
  const parts = [];
  if (contact.last_name) parts.push(contact.last_name);
  if (contact.first_name) parts.push(contact.first_name);
  if (contact.middle_name) parts.push(contact.middle_name);
  return parts.join(' ') || 'Без имени';
};

/**
 * ContactsPage
 */
const ContactsPage = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Пагинация
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Загрузка контактов
  const loadContacts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
      });

      const response = await fetch(`/api/contacts?${params}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('У вас нет доступа к разделу контактов');
        }
        throw new Error('Не удалось загрузить контакты');
      }

      const data = await response.json();

      // Трансформируем данные для таблицы
      const transformedData = data.items.map((contact) => ({
        ...contact,
        full_name: formatFullName(contact),
        phone_search: contact.phones?.map(p => p.number).join(' ') || '',
      }));

      setContacts(transformedData);
      setTotalPages(data.total_pages);
      setTotal(data.total);
    } catch (err) {
      console.error('Failed to load contacts:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  // Клик по строке - переход к организации
  const handleRowClick = (row) => {
    navigate(`/organizations/${row.organization_id}`);
  };

  // Рендер ячейки
  const renderCell = (row, column) => {
    switch (column.key) {
      case 'full_name':
        return (
          <Group gap="xs" wrap="nowrap">
            <Text fw={500} fz="sm">
              {row.full_name}
            </Text>
            {row.is_primary && (
              <Badge
                size="xs"
                variant="filled"
                color="green"
                radius="xl"
                leftSection={<IconStar size={10} />}
              >
                Осн.
              </Badge>
            )}
          </Group>
        );

      case 'organization_name':
        return (
          <Anchor
            component="button"
            fz="sm"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/organizations/${row.organization_id}`);
            }}
          >
            {row.organization_name}
          </Anchor>
        );

      case 'position':
        return row.position ? (
          <Text fz="sm">{row.position}</Text>
        ) : (
          <Text fz="sm" c="dimmed">—</Text>
        );

      case 'phone':
        if (!row.phones || row.phones.length === 0) {
          return <Text fz="sm" c="dimmed">—</Text>;
        }
        const primaryPhone = row.phones[0];
        return (
          <Group gap={4} wrap="nowrap">
            <Anchor
              href={`tel:${primaryPhone.number}`}
              fz="sm"
              onClick={(e) => e.stopPropagation()}
            >
              <Group gap={4}>
                <IconPhone size={14} />
                {formatPhoneDisplay(primaryPhone.number)}
              </Group>
            </Anchor>
            {row.phones.length > 1 && (
              <Text fz="xs" c="dimmed">
                (+{row.phones.length - 1})
              </Text>
            )}
          </Group>
        );

      case 'email':
        return row.email ? (
          <Anchor
            href={`mailto:${row.email}`}
            fz="sm"
            onClick={(e) => e.stopPropagation()}
          >
            <Group gap={4}>
              <IconMail size={14} />
              {row.email}
            </Group>
          </Anchor>
        ) : (
          <Text fz="sm" c="dimmed">—</Text>
        );

      case 'messengers':
        const hasMessengers = row.messengers && (
          row.messengers.telegram || row.messengers.whatsapp || row.messengers.viber
        );
        if (!hasMessengers) {
          return <Text fz="sm" c="dimmed">—</Text>;
        }
        return (
          <Group gap="xs">
            {row.messengers.telegram && (
              <Anchor
                href={
                  row.messengers.telegram.startsWith('@')
                    ? `https://t.me/${row.messengers.telegram.slice(1)}`
                    : `https://t.me/${row.messengers.telegram}`
                }
                target="_blank"
                onClick={(e) => e.stopPropagation()}
              >
                <IconBrandTelegram size={18} color={tokens.colors.brand.primary} />
              </Anchor>
            )}
            {row.messengers.whatsapp && (
              <Anchor
                href={`https://wa.me/${row.messengers.whatsapp.replace(/\D/g, '')}`}
                target="_blank"
                onClick={(e) => e.stopPropagation()}
              >
                <IconBrandWhatsapp size={18} color="#25D366" />
              </Anchor>
            )}
          </Group>
        );

      default:
        return undefined;
    }
  };

  if (loading && contacts.length === 0) {
    return (
      <Box px="md" py="md">
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      </Box>
    );
  }

  if (error) {
    return (
      <Box px="md" py="md">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Ошибка"
          color="red"
          variant="light"
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box px="md" py="md">
      <Stack gap="md">
        {/* Заголовок */}
        <Group justify="space-between" align="flex-end">
          <Box>
            <Title order={2} style={{ color: tokens.colors.brand.primary }}>
              Контакты
            </Title>
            <Text c="dimmed" size="sm">
              Всего: {total}
            </Text>
          </Box>
        </Group>

        {/* Таблица */}
        <DataTable
          columns={columns}
          data={contacts}
          onRowClick={handleRowClick}
          searchFields={searchFields}
          renderCell={renderCell}
          sortable={true}
          emptyMessage="Контакты не найдены"
          minWidth={900}
        />

        {/* Пагинация */}
        {totalPages > 1 && (
          <Group justify="space-between" mt="md">
            <Group gap="xs">
              <Text size="sm" c="dimmed">Показывать:</Text>
              <Select
                value={pageSize.toString()}
                onChange={(value) => {
                  setPageSize(Number(value));
                  setPage(1);
                }}
                data={[
                  { value: '25', label: '25' },
                  { value: '50', label: '50' },
                  { value: '100', label: '100' },
                ]}
                size="xs"
                style={{ width: 80 }}
              />
            </Group>

            <Pagination
              total={totalPages}
              value={page}
              onChange={setPage}
              size="sm"
              siblings={1}
              boundaries={1}
            />

            <Text size="sm" c="dimmed">
              Страница {page} из {totalPages}
            </Text>
          </Group>
        )}
      </Stack>
    </Box>
  );
};

export default ContactsPage;
